import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Returns every page-level scroll container to the top on navigation.
 *
 * The app shell is `h-screen overflow-hidden`, so the window itself never
 * scrolls - the scrollbar belongs to a container inside the layout, which
 * stays mounted across route changes and therefore keeps its old scrollTop.
 * `window.scrollTo` alone is a no-op here. Containers opt in with
 * `data-scroll-root`; inner scrollers (dropdown lists, file lists) are left
 * alone deliberately.
 */
export const ScrollToTop = () => {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    // A jump is the honest default when the outgoing page's scroll position is
    // meaningless on the incoming one; smooth-scrolling a container whose
    // content just swapped animates through unrelated layout.
    const behavior: ScrollBehavior = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
      ? 'auto'
      : 'smooth';

    window.scrollTo({ top: 0, left: 0, behavior });

    document.querySelectorAll<HTMLElement>('[data-scroll-root]').forEach((el) => {
      if (el.scrollTop === 0) return;
      el.scrollTo({ top: 0, left: 0, behavior });
    });
  }, [pathname]);

  return null;
};

export default ScrollToTop;
